import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy } from 'lucide-react';
import { API_URL as API } from '../config/api';

const RANK_COLORS = {
  1: { bg: 'linear-gradient(135deg,#FEF3C7 0%,#FDE68A 100%)', border: '#F59E0B', text: '#92400E', icon: '🥇', label: '1st Place' },
  2: { bg: 'linear-gradient(135deg,#F3F4F6 0%,#E5E7EB 100%)', border: '#9CA3AF', text: '#374151', icon: '🥈', label: '2nd Place' },
  3: { bg: 'linear-gradient(135deg,#FEF0E6 0%,#FDCFAA 100%)', border: '#D97706', text: '#92400E', icon: '🥉', label: '3rd Place' },
};

export default function TournamentLeaderboard({ tournamentId, token }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tournamentId) return;
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/tournaments/${tournamentId}/leaderboard`, { headers })
      .then(res => setEntries(res.data || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [tournamentId]);

  if (loading) return (
    <div style={{ padding: '1rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.85rem' }}>
      Loading leaderboard...
    </div>
  );

  if (!entries.length) return null;

  const top3 = entries.filter(e => e.rank <= 3).sort((a, b) => a.rank - b.rank);
  const rest = entries.filter(e => e.rank > 3).sort((a, b) => a.rank - b.rank);
  // Podium visual order: 2nd (left) → 1st (tallest/centre) → 3rd (right)
  const podium = [
    top3.find(e => e.rank === 2),
    top3.find(e => e.rank === 1),
    top3.find(e => e.rank === 3),
  ].filter(Boolean);

  return (
    <div style={{ marginTop: '0.5rem' }}>
      {/* Section Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg,#FDE68A,#F59E0B)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Trophy size={20} color="#92400E" />
        </div>
        <div>
          <div style={{ fontWeight: '900', color: '#0D1F0F', fontSize: '1.05rem' }}>Tournament Results</div>
          <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>Official Leaderboard & Winners</div>
        </div>
      </div>

      {/* Top-3 Podium */}
      {podium.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '8px', marginBottom: '1.25rem' }}>
          {podium.map((entry) => {
            const colors = RANK_COLORS[entry.rank] || RANK_COLORS[3];
            const isFirst = entry.rank === 1;
            return (
              <div key={entry._id} style={{
                flex: 1, maxWidth: '160px',
                background: colors.bg, border: `2px solid ${colors.border}`,
                borderRadius: '16px', padding: isFirst ? '1.1rem 0.75rem' : '0.75rem 0.6rem',
                textAlign: 'center',
                boxShadow: isFirst ? `0 8px 24px ${colors.border}40` : '0 4px 12px rgba(0,0,0,0.06)',
                transform: isFirst ? 'translateY(-12px)' : 'none',
              }}>
                <div style={{ fontSize: isFirst ? '2.2rem' : '1.8rem', marginBottom: '4px' }}>{colors.icon}</div>
                <div style={{ fontWeight: '900', color: colors.text, fontSize: isFirst ? '0.9rem' : '0.8rem', marginBottom: '2px' }}>{colors.label}</div>
                <div style={{ fontWeight: '800', color: '#111827', fontSize: isFirst ? '0.95rem' : '0.85rem', lineHeight: 1.2, marginBottom: '4px' }}>
                  {entry.team_name || '—'}
                </div>
                {entry.captain_name && (
                  <div style={{ fontSize: '0.7rem', color: '#6B7280', marginBottom: '4px' }}>Captain: {entry.captain_name}</div>
                )}
                {entry.prize && (
                  <div style={{ background: 'rgba(255,255,255,0.7)', color: colors.text, fontSize: '0.7rem', fontWeight: '800', padding: '3px 8px', borderRadius: '20px', display: 'inline-block', marginTop: '2px' }}>
                    🏆 {entry.prize}
                  </div>
                )}
                {entry.notes && (
                  <div style={{ fontSize: '0.65rem', color: '#9CA3AF', marginTop: '4px', fontStyle: 'italic' }}>{entry.notes}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Additional entries rank > 3 */}
      {rest.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {rest.map((entry) => (
            <div key={entry._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#F9FAFB', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '10px 14px' }}>
              <div style={{ minWidth: '32px', height: '32px', borderRadius: '8px', background: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', color: '#374151', fontSize: '0.9rem' }}>#{entry.rank}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '800', color: '#1F2937', fontSize: '0.9rem' }}>{entry.rank_label}</div>
                {entry.team_name && (
                  <div style={{ fontSize: '0.8rem', color: '#4B5563' }}>{entry.team_name}{entry.captain_name ? ` · ${entry.captain_name}` : ''}</div>
                )}
                {entry.notes && (
                  <div style={{ fontSize: '0.72rem', color: '#9CA3AF', fontStyle: 'italic', marginTop: '1px' }}>{entry.notes}</div>
                )}
              </div>
              {entry.prize && (
                <div style={{ background: '#FEF3C7', color: '#D97706', fontSize: '0.75rem', fontWeight: '800', padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                  🏆 {entry.prize}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
